import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useProductRefreshUiTheme } from 'features/app_shell/product_refresh/useProductRefreshUiTheme'
import { MI_MEETINGS_TOUR, StepThree, handleCheckStepEnabled } from 'features/meeting_intelligence'
import { useHasSpotlight } from 'features/meetings/hooks'
import type { Meeting } from 'features/meetings/types'

import { Buffers } from './Buffers'
import { EditBufferPopup } from './edit_buffer_popup'
import { MeetingCardContainer } from './MeetingCardContainer'
import { MeetingCardContent } from './MeetingCardContent'
import { MeetingCardLink } from './MeetingCardLink'
import { useBuffers } from './useBuffers'

interface MeetingCardProps {
  event: Meeting
  otherEventsOnDay?: Meeting[]
  selectedEventId?: string
  isFirst?: boolean
  isLast?: boolean
  showBuffers?: boolean
  isNext?: boolean
  anyInProgress?: boolean
  isMostRecentInProgress?: boolean
  packageName?: string | null
  onboardingTourMeetingRefCallback?: (el: HTMLElement | null) => void
  onboardingTourNotetakerRefCallback?: (el: HTMLElement | null) => void
  isFirstUpcomingForTour?: boolean
  linkPathname?: string
}

/**
 * Renders a single meeting card row within the meetings list.
 *
 * Composes the card container, link wrapper, content, optional buffer rows,
 * and the edit-buffer popup. Manages hover and compact layout state, and
 * integrates with the Meeting Intelligence onboarding tour step anchors.
 *
 * @param event - The meeting to display
 * @param otherEventsOnDay - Other meetings on the same day, used for live status badge logic
 * @param selectedEventId - UUID of the currently selected meeting, used to apply active styling
 * @param isFirst - Whether this card is the first in its group (affects border radius)
 * @param isLast - Whether this card is the last in its group (affects border radius)
 * @param showBuffers - Whether to render before/after buffer rows and the edit-buffer popup
 * @param isNext - Whether this is the next upcoming meeting (shows the current time indicator above it)
 * @param anyInProgress - Whether any meeting in the list is currently in progress
 * @param isMostRecentInProgress - Whether this is the most recent in-progress meeting (affects join button style)
 * @param packageName - Package booking name to display on the card, if applicable
 * @param onboardingTourMeetingRefCallback - Ref callback for the MI tour step-one anchor
 * @param onboardingTourNotetakerRefCallback - Ref callback for the MI tour notetaker anchor
 * @param isFirstUpcomingForTour - Whether this card is the tour's target upcoming meeting
 */
export const MeetingCard = ({
  event,
  otherEventsOnDay,
  selectedEventId,
  isFirst,
  isLast,
  showBuffers,
  isNext,
  anyInProgress,
  isMostRecentInProgress = false,
  packageName = null,
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  isFirstUpcomingForTour = false,
  linkPathname,
}: MeetingCardProps) => {
  const { uuid, key: eventKey } = event
  const hasSpotlight = useHasSpotlight(eventKey)

  const [mouseHovered, setMouseHovered] = useState(false)
  const [childButtonHovered, setChildButtonHovered] = useState(false)

  const isSelected = uuid === selectedEventId

  const location = useLocation()
  const showStepOne = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_ONE)
  const showStepThree = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_THREE)

  const hovered = mouseHovered || (showStepThree && hasSpotlight)

  const isPhone = useDeviceType('isPhone')
  const { startBufferProps, endBufferProps, popupProps } = useBuffers(event)

  const isMobileSpotlightStepThree = hasSpotlight && showStepThree && isPhone
  const isBrandRefresh = useProductRefreshUiTheme()

  return (
    <>
      <MeetingCardContainer
        data-testid={`meeting-card-${uuid}`}
        isFirst={isFirst}
        isLast={isLast}
      >
        {showBuffers && startBufferProps && (
          <Buffers {...startBufferProps} />
        )}
        <MeetingCardLink
          event={event}
          active={isSelected}
          isChildHovered={childButtonHovered || showStepOne}
          spotlight={showStepThree && hasSpotlight}
          linkPathname={linkPathname}
          onMouseEnter={() => setMouseHovered(true)}
          onMouseLeave={() => setMouseHovered(false)}
          isBrandRefresh={isBrandRefresh}
        >
          <MeetingCardContent
            event={event}
            otherEventsOnDay={otherEventsOnDay}
            isNext={isNext}
            anyInProgress={anyInProgress}
            isMostRecentInProgress={isMostRecentInProgress}
            packageName={packageName}
            onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback}
            onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback}
            isFirstUpcomingForTour={isFirstUpcomingForTour}
            hovered={hovered}
            isSelected={isSelected}
            setChildButtonHovered={setChildButtonHovered}
          />
        </MeetingCardLink>
        {isMobileSpotlightStepThree && <StepThree isVisible={hasSpotlight} />}
        {showBuffers && endBufferProps && (
          <Buffers {...endBufferProps} />
        )}
      </MeetingCardContainer>
      {showBuffers && (
        <EditBufferPopup
          {...popupProps}
        />
      )}
    </>
  )
}
